import type { Waypoint, WaypointsReplaceRequest } from '@xingroute/schema'
import { nearestPointOnTrack, type TrackPointLite } from './snapToTrack.js'

// Merged-waypoint algorithm shared by the editor's WaypointEditorList
// and the read view's WaypointReadList. Pure functions only — no React
// here. Both lists render the same sort order / system row derivation.
//
// Per `wiki/decisions/10-route-spatial-and-governance.md` §I:
//   - Every GPX gets 5 system waypoints derived from the track itself:
//     start / end / highest / lowest / mid. They follow the GPX and
//     cannot be edited or deleted.
//   - User waypoints are displayed in their projected track position
//     alongside system waypoints.

export type EditableWaypoint = Waypoint & { auto?: boolean }
export type WaypointReplace = WaypointsReplaceRequest['waypoints'][number]

export type SystemWaypointKind = 'start' | 'end' | 'highest' | 'lowest' | 'mid'

export interface SystemWaypoint {
  id: string
  kind: SystemWaypointKind
  lat: number
  lon: number
  ele?: number
  index: number
}

export type MergedRow =
  | {
      kind: 'system'
      trackIndex: number
      system: SystemWaypoint
      rowKey: string
    }
  | {
      kind: 'user'
      trackIndex: number
      waypoint: EditableWaypoint
      rowKey: string
    }

export function waypointsEqual(a: readonly Waypoint[], b: readonly Waypoint[]): boolean {
  if (a.length !== b.length) return false
  return a.every((left, index) => {
    const right = b[index]
    if (!right) return false
    return (
      left.id === right.id &&
      left.lat === right.lat &&
      left.lon === right.lon &&
      left.ele === right.ele &&
      left.name === right.name &&
      left.desc === right.desc &&
      left.name_en === right.name_en &&
      left.desc_en === right.desc_en &&
      left.note === right.note &&
      left.captured_at === right.captured_at &&
      left.sym === right.sym &&
      left.kind === right.kind &&
      left.distance_along_track === right.distance_along_track &&
      left.auto === right.auto
    )
  })
}

export function waypointToReplaceRequest(
  waypoint: EditableWaypoint | WaypointReplace,
): WaypointReplace {
  return {
    ...(waypoint.id ? { id: waypoint.id } : {}),
    lat: waypoint.lat,
    lon: waypoint.lon,
    ...(waypoint.ele !== undefined ? { ele: waypoint.ele } : {}),
    ...(waypoint.name !== undefined ? { name: waypoint.name } : {}),
    ...(waypoint.desc !== undefined ? { desc: waypoint.desc } : {}),
    ...(waypoint.name_en !== undefined ? { name_en: waypoint.name_en } : {}),
    ...(waypoint.desc_en !== undefined ? { desc_en: waypoint.desc_en } : {}),
    ...(waypoint.note !== undefined ? { note: waypoint.note } : {}),
    ...(waypoint.captured_at !== undefined ? { captured_at: waypoint.captured_at } : {}),
    ...(waypoint.distance_along_track !== undefined
      ? { distance_along_track: waypoint.distance_along_track }
      : {}),
    ...(waypoint.sym !== undefined ? { sym: waypoint.sym } : {}),
    ...(waypoint.kind !== undefined ? { kind: waypoint.kind } : {}),
  }
}

export const SYSTEM_KIND_TO_SYM: Record<SystemWaypointKind, string> = {
  start: 'Start',
  end: 'End',
  highest: 'Summit',
  lowest: 'Valley',
  mid: 'XR-Mid',
}

export function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6_371_000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)))
}

export function midpointByDistanceIndex(track: readonly TrackPointLite[]): number {
  if (track.length < 2) return 0
  const cum: number[] = new Array(track.length).fill(0)
  for (let i = 1; i < track.length; i += 1) {
    const prev = track[i - 1]!
    const cur = track[i]!
    cum[i] = cum[i - 1]! + haversineMeters(prev.lat, prev.lon, cur.lat, cur.lon)
  }
  const half = cum[cum.length - 1]! / 2
  for (let i = 0; i < cum.length; i += 1) {
    if (cum[i]! >= half) return i
  }
  return Math.floor(track.length / 2)
}

export function computeSystemWaypoints(track: readonly TrackPointLite[]): SystemWaypoint[] {
  if (track.length === 0) return []
  const out: SystemWaypoint[] = []
  const push = (kind: SystemWaypointKind, index: number) => {
    const p = track[index]
    if (!p) return
    out.push({ id: `system-${kind}`, kind, lat: p.lat, lon: p.lon, ele: p.ele, index })
  }
  push('start', 0)
  push('end', track.length - 1)
  let highestIdx = -1
  let lowestIdx = -1
  let highestEle = -Infinity
  let lowestEle = Infinity
  for (let i = 0; i < track.length; i += 1) {
    const ele = track[i]?.ele
    if (ele === undefined || Number.isNaN(ele)) continue
    if (ele > highestEle) {
      highestEle = ele
      highestIdx = i
    }
    if (ele < lowestEle) {
      lowestEle = ele
      lowestIdx = i
    }
  }
  if (highestIdx >= 0) push('highest', highestIdx)
  if (lowestIdx >= 0) push('lowest', lowestIdx)
  const midIdx = midpointByDistanceIndex(track)
  push('mid', midIdx)
  out.sort((a, b) => a.index - b.index)
  return out
}

export function computeMergedRows(
  waypoints: readonly EditableWaypoint[],
  track: readonly TrackPointLite[] | undefined,
): MergedRow[] {
  const rows: MergedRow[] = []
  const systemWaypoints = track && track.length > 0 ? computeSystemWaypoints(track) : []

  for (const w of waypoints) {
    const projected =
      track && track.length > 0
        ? (nearestPointOnTrack(w.lat, w.lon, track)?.index ?? Number.MAX_SAFE_INTEGER)
        : Number.MAX_SAFE_INTEGER
    rows.push({
      kind: 'user',
      trackIndex: projected,
      waypoint: w,
      rowKey: `user-${w.id}`,
    })
  }
  for (const sw of systemWaypoints) {
    rows.push({ kind: 'system', trackIndex: sw.index, system: sw, rowKey: `system-${sw.kind}` })
  }
  rows.sort((a, b) => a.trackIndex - b.trackIndex)
  return rows
}

export function systemLabelFor(kind: SystemWaypointKind, t: (key: string) => string): string {
  switch (kind) {
    case 'start':
      return t('editor.waypointList.systemStart')
    case 'end':
      return t('editor.waypointList.systemEnd')
    case 'highest':
      return t('editor.waypointList.systemHighest')
    case 'lowest':
      return t('editor.waypointList.systemLowest')
    case 'mid':
      return t('editor.waypointList.systemMid')
  }
}

export function newWaypointId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return '10000000-1000-4000-8000-000000000000'.replace(/[018]/g, (c) =>
    (Number(c) ^ (Math.random() * 16)).toString(16),
  )
}
